// generateItemListJsonLd.js
import {
  DEFAULT_LANGUAGE,
  localeConvertor,
  PREFERRED_LOCALES,
} from '../resource/i18nCommon.js';
import { getLanguageTranslationKey } from '../resource/getCurrentLanguage.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getI18nInstance } from '../resource/i18n.js';

/**
 * Convert locale to BCP47 format
 * @param {string} lang - Language code
 * @returns {string} BCP47 formatted language code
 */
const localeToBCP47 = lang => localeConvertor({ locale: lang, isBCP47: true });

/**
 * Build name/value object with language tag
 * @param {string} lang - Language code
 * @param {Function} translateFn - Translation function that takes language code
 * @returns {Object} Object with @value and @language properties
 */
const buildNameValueByLang = (lang, translateFn) => ({
  '@value': translateFn(lang),
  '@language': localeToBCP47(lang),
});

/**
 * Build array of alternate values for all alternate languages
 * @param {Array<string>} alternateLanguages - Array of alternate language codes
 * @param {Function} translateFn - Translation function that takes language code
 * @returns {Array<Object>} Array of objects with @value and @language properties
 */
const buildAlternateValues = (alternateLanguages, translateFn) =>
  alternateLanguages.map(lang => buildNameValueByLang(lang, translateFn));

/**
 * Generate ItemList JSON-LD for list pages with multi-language support
 * This function is designed for Next.js RRv7 server-side rendering
 *
 * @param {Object} params
 * @param {Object} params.fullUrl - Full URL object with origin and pathname
 * @param {string} params.pageId - Page identifier for @id (e.g., 'feed', 'itemlist')
 * @param {string} params.titleKey - Translation key for title
 * @param {string} params.descriptionKey - Translation key for description (optional)
 * @param {Array<string>} params.itemIds - Array of item IDs
 * @param {Function} params.getItemId - Function to generate item @id from itemId and origin
 * @param {Array<string>} params.supportedLanguages - Array of supported language codes (optional, will use PREFERRED_LOCALES if not provided)
 * @returns {Promise<Object>} JSON-LD object with CollectionPage, ItemList, and BreadcrumbList
 */
export const generateItemListJsonLd = async ({
  fullUrl,
  pageId = 'itemlist',
  titleKey,
  descriptionKey,
  itemIds = [],
  getItemId = (itemId, origin) => `${origin}/item/${itemId}`,
  supportedLanguages,
}) => {
  // Get i18n instance
  const { i18n } = await getI18nInstance();
  const t = i18n.t;
  const { origin, pathname } = fullUrl;

  // Use all available languages if not provided
  // Default to PREFERRED_LOCALES which includes all supported languages
  const languages = supportedLanguages || PREFERRED_LOCALES;

  // Only include languages that have loaded resource bundles
  const availableLanguages = languages.filter(lang =>
    i18n.hasResourceBundle(lang, TranslationNamespace.SEO)
  );

  // If no languages have loaded bundles, use current language only
  const effectiveLanguages =
    availableLanguages.length > 0 ? availableLanguages : [i18n.language];

  const alternateLanguages = effectiveLanguages.filter(
    lang => DEFAULT_LANGUAGE !== lang
  );

  const pageUrl = `${origin}${pathname}`;

  // Helper function to get translated title by language
  const getTitleByLang = lang => {
    return t(titleKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });
  };

  // Helper function to get translated description by language
  const getDescriptionByLang = lang => {
    return t(descriptionKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });
  };

  // Helper function to get home title by language
  const getHomeTitleByLang = lang => {
    return t('seo-home-title', {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        inLanguage: effectiveLanguages.map(lang => ({
          '@type': 'Language',
          name: t(getLanguageTranslationKey({ language: lang }), {
            ns: TranslationNamespace.SEO,
          }),
          alternateName: localeToBCP47(lang),
        })),
        name: buildNameValueByLang(DEFAULT_LANGUAGE, getTitleByLang),
        ...(alternateLanguages.length > 0 && {
          alternateName: buildAlternateValues(
            alternateLanguages,
            getTitleByLang
          ),
        }),
        ...(descriptionKey && {
          description: buildNameValueByLang(
            DEFAULT_LANGUAGE,
            getDescriptionByLang
          ),
          ...(alternateLanguages.length > 0 && {
            alternateDescription: buildAlternateValues(
              alternateLanguages,
              getDescriptionByLang
            ),
          }),
        }),
        breadcrumb: {
          '@id': `${pageUrl}#breadcrumb`,
        },
        mainEntity: {
          '@id': `${pageUrl}#${pageId}`,
        },
      },
      {
        '@type': 'ItemList',
        '@id': `${pageUrl}#${pageId}`,
        url: pageUrl,
        itemListElement: itemIds.map((itemId, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@id': getItemId(itemId, origin),
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: buildNameValueByLang(DEFAULT_LANGUAGE, getHomeTitleByLang),
            ...(alternateLanguages.length > 0 && {
              alternateName: buildAlternateValues(
                alternateLanguages,
                getHomeTitleByLang
              ),
            }),
            item: {
              '@id': `${origin}/#website`,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: buildNameValueByLang(DEFAULT_LANGUAGE, getTitleByLang),
            ...(alternateLanguages.length > 0 && {
              alternateName: buildAlternateValues(
                alternateLanguages,
                getTitleByLang
              ),
            }),
            item: {
              '@id': `${pageUrl}#webpage`,
            },
          },
        ],
      },
    ],
  };
};

export default generateItemListJsonLd;
